import { isDesignOnlyLineType } from './orderStatusSync';
import {
  getScreenPrintBatchKey,
  getInkFingerprint,
  normalizeDesignNameForBatch
} from './screenPrintBatching';
import { lineCogsQuantity } from './sizeGrids';

// Constants for time calculations
const DAILY_MINUTES = 420;         // 7 hours
const MAINTENANCE_TIME = 15;       // 15 minutes
const AVAILABLE_MINUTES = DAILY_MINUTES - MAINTENANCE_TIME;  // 405 minutes
const MINIMUM_VIABLE_GAP = 30;     // Don't try to fill gaps smaller than 30 minutes
const MAX_SCREEN_PRINT_COLORS = 8;
/** Extra time when the same design is run again with different inks (washup / swap). */
const SCREEN_INK_SWAP_MINUTES = 18;

/** Firestore Timestamp | Date | string | seconds → ms. Invalid / missing → +Infinity (sort last). */
const dueDateToMillis = (value) => {
  if (value == null || value === '') return Number.POSITIVE_INFINITY;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, d] = value.trim().split('-').map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

const compareJobsByDueDate = (a, b) => {
  const da = dueDateToMillis(a?.dueDate);
  const db = dueDateToMillis(b?.dueDate);
  if (da !== db) return da - db;
  const oa = String(a?.orderNumber ?? '');
  const ob = String(b?.orderNumber ?? '');
  if (oa !== ob) return oa.localeCompare(ob, undefined, { numeric: true });
  return String(a?.id || '').localeCompare(String(b?.id || ''));
};

const calculateScreenPrintTime = (pieces, frontColors = 0, backColors = 0, otherColors = 0) => {
    let totalMinutes = 0;
    
    if (frontColors > 0) {
        const frontSetupMinutes = frontColors * 5;  // 5 min setup per color
        const frontPrintMinutes = (pieces / 250) * 60;  // 250 pieces per hour
        const frontTeardownMinutes = 10;
        totalMinutes += frontSetupMinutes + frontPrintMinutes + frontTeardownMinutes;
    }
    
    if (backColors > 0) {
        const backSetupMinutes = backColors * 5;
        const backPrintMinutes = (pieces / 250) * 60;
        const backTeardownMinutes = 10;
        totalMinutes += backSetupMinutes + backPrintMinutes + backTeardownMinutes;
    }
    
    if (otherColors > 0) {
        const otherSetupMinutes = otherColors * 5;
        const otherPrintMinutes = (pieces / 250) * 60;
        const otherTeardownMinutes = 10;
        totalMinutes += otherSetupMinutes + otherPrintMinutes + otherTeardownMinutes;
    }
    
    return Math.ceil(totalMinutes);
};

const calculateDTGTime = (pieces, locations) => {
    const setupTimePerJob = 5;         // 5 minutes setup per job
    const printTimePerLocation = 2.5;  // 2.5 minutes per location per piece
    
    return setupTimePerJob + (pieces * locations * printTimePerLocation);
};

const calculateDTFTime = (pieces, locations = 1) => {
    // 2 min heat-press per location per piece (front+back+other = 6 min/garment)
    const heatPressMinutesPerLocation = 2;
    const locationCount = Math.max(1, Number(locations) || 1);
    return pieces * locationCount * heatPressMinutesPerLocation;
};

const EMBROIDERY_SETUP_MINUTES = 30;       // hooping / framing / machine setup per job
const EMBROIDERY_EFFECTIVE_SPM = 500;      // effective stitches per minute (incl. stoppages)
const EMBROIDERY_HEADS = 8;               // heads running in parallel
const EMBROIDERY_DEFAULT_STITCHES = 7500;  // used when a line has no stitch count

/**
 * Embroidery minutes for a job.
 * Run time assumes 8 heads stitch in parallel (effective throughput ×8).
 * @param {number} pieces
 * @param {number} [stitchesPerPiece] total stitches on the garment (all locations). Defaults to 7.5k.
 */
const calculateEmbroideryTime = (pieces, stitchesPerPiece = EMBROIDERY_DEFAULT_STITCHES) => {
    const qty = Number(pieces) || 0;
    if (qty <= 0) return 0;

    const stitches = Math.max(
        0,
        Number(stitchesPerPiece) > 0 ? Number(stitchesPerPiece) : EMBROIDERY_DEFAULT_STITCHES
    );
    // Parallel heads: wall-clock run ≈ (pieces × stitches) / (SPM × heads)
    const runMinutes = (qty * stitches) / (EMBROIDERY_EFFECTIVE_SPM * EMBROIDERY_HEADS);
    return EMBROIDERY_SETUP_MINUTES + runMinutes;
};

/** Prefer explicit stitch fields; otherwise default. */
const resolveEmbroideryStitchesPerPiece = (line = {}) => {
    const direct =
        Number(line.stitchCount) ||
        Number(line.stitches) ||
        Number(line.stitchesPerPiece) ||
        0;
    if (direct > 0) return direct;

    // Optional per-location stitch counts if present on the line
    const locationStitches = ['front', 'back', 'other'].reduce((sum, key) => {
        const n =
            Number(line[`${key}StitchCount`]) ||
            Number(line[`${key}Stitches`]) ||
            0;
        return sum + (n > 0 ? n : 0);
    }, 0);
    if (locationStitches > 0) return locationStitches;

    return EMBROIDERY_DEFAULT_STITCHES;
};

const canBeScreenPrinted = (frontColors = 0, backColors = 0, otherColors = 0) => {
    return frontColors <= MAX_SCREEN_PRINT_COLORS && 
           backColors <= MAX_SCREEN_PRINT_COLORS && 
           otherColors <= MAX_SCREEN_PRINT_COLORS;
};

const isDTGJob = (line) => {
    const lineType = line.type?.toLowerCase();
    return lineType === 'dtg';
};

const isDTFJob = (line) => {
    return line.type?.toLowerCase() === 'dtf';
};

const isEmbroideryJob = (line) => {
    return line.type?.toLowerCase() === 'embroidery';
};

const isScreenPrintJob = (line) => {
    const lineType = line.type?.toLowerCase();
    // Treat null/undefined type as screen print (the default)
    // Also catch lines that fall through other checks
    return !lineType || lineType === 'screen' || lineType === 'screen print';
};

const calculateOptimalPrintlist = (lines) => {
    const productionLines = (lines || []).filter((line) => !isDesignOnlyLineType(line.type));
    // Sort lines by due date first (Firestore Timestamps must use toMillis/toDate — not new Date(ts))
    const sortedLines = [...productionLines].sort(compareJobsByDueDate);

    // Calculate time for each line and add it to the object
    const linesWithTimes = sortedLines.map(line => {
        const frontColors = Array.isArray(line.frontInkColors) ? line.frontInkColors.length : 0;
        const backColors = Array.isArray(line.backInkColors) ? line.backInkColors.length : 0;
        const otherColors = Array.isArray(line.otherInkColors) ? line.otherInkColors.length : 0;
        const pieces = lineCogsQuantity(line);
        
        // For DTG/DTF, use printLocations array; for others, count ink color locations
        const rawLocations = line.printLocations;
        const printLocations = Array.isArray(rawLocations)
          ? rawLocations
          : rawLocations && typeof rawLocations === 'object'
            ? Object.values(rawLocations)
            : [];
        const inkColorLocations = (frontColors > 0 ? 1 : 0) + 
                                  (backColors > 0 ? 1 : 0) + 
                                  (otherColors > 0 ? 1 : 0);
        
        let printTime = 0;
        let suggestedMethod = 'Screen Print';
        
        if (isDTGJob(line)) {
            // Use printLocations for DTG, fallback to inkColorLocations for legacy data
            const locations = printLocations.length > 0 ? printLocations.length : inkColorLocations;
            printTime = calculateDTGTime(pieces, locations || 1);
            suggestedMethod = 'DTG';
        } else if (isDTFJob(line)) {
            // Use printLocations for DTF, fallback to inkColorLocations for legacy data
            const locations = printLocations.length > 0 ? printLocations.length : inkColorLocations;
            printTime = calculateDTFTime(pieces, locations || 1);
            suggestedMethod = 'DTF';
        } else if (isEmbroideryJob(line)) {
            printTime = calculateEmbroideryTime(
                pieces,
                resolveEmbroideryStitchesPerPiece(line)
            );
            suggestedMethod = 'Embroidery';
        } else {
            printTime = calculateScreenPrintTime(pieces, frontColors, backColors, otherColors);
            suggestedMethod = canBeScreenPrinted(frontColors, backColors, otherColors) ? 'Screen Print' : 'DTG';
        }

        const safeTime = Number.isFinite(printTime) ? Math.max(0, printTime) : 0;

        return {
            ...line,
            totalQuantity: pieces > 0 ? pieces : (Number(line.totalQuantity) || 0),
            estimatedTime: safeTime,
            suggestedMethod
        };
    });

    // Group by print method
    const dtgJobs = linesWithTimes.filter(line => isDTGJob(line)).sort(compareJobsByDueDate);
    const dtfJobs = linesWithTimes.filter(line => isDTFJob(line)).sort(compareJobsByDueDate);
    const embroideryJobs = linesWithTimes.filter(line => isEmbroideryJob(line)).sort(compareJobsByDueDate);
    let screenPrintJobs = linesWithTimes.filter(line => isScreenPrintJob(line));

    // Screen print: same normalized design + same F/B/O ink sets = one setup/teardown, one run for total pieces (any order).
    const screenPrintByBatch = new Map();
    for (const line of screenPrintJobs) {
        const key = getScreenPrintBatchKey(line);
        if (!screenPrintByBatch.has(key)) screenPrintByBatch.set(key, []);
        screenPrintByBatch.get(key).push(line);
    }
    for (const [, group] of screenPrintByBatch) {
        if (group.length <= 1) continue;
        const totalPieces = group.reduce((sum, l) => sum + lineCogsQuantity(l), 0);
        const maxFront = Math.max(...group.map(l => (Array.isArray(l.frontInkColors) ? l.frontInkColors.length : 0)), 0);
        const maxBack = Math.max(...group.map(l => (Array.isArray(l.backInkColors) ? l.backInkColors.length : 0)), 0);
        const maxOther = Math.max(...group.map(l => (Array.isArray(l.otherInkColors) ? l.otherInkColors.length : 0)), 0);
        const totalTime = calculateScreenPrintTime(totalPieces, maxFront, maxBack, maxOther);
        for (const line of group) {
            const qty = lineCogsQuantity(line);
            const pct = totalPieces > 0 ? qty / totalPieces : 1 / group.length;
            line.estimatedTime = Math.max(1, Math.ceil(pct * totalTime));
        }
    }

    // Same design, different ink than the last time we saw that design in schedule order: ink washup / swap.
    // Return this order so Today's Goal / Up Next follow due date (not raw fetch order).
    screenPrintJobs = [...screenPrintJobs].sort(compareJobsByDueDate);
    const lastInkByDesign = new Map();
    for (const line of screenPrintJobs) {
        const dKey = normalizeDesignNameForBatch(line.designName) || '__unnamed__';
        const fp = getInkFingerprint(line);
        const prevFp = lastInkByDesign.get(dKey);
        if (prevFp !== undefined && prevFp !== fp) {
            line.estimatedTime = Math.max(1, (line.estimatedTime || 0) + SCREEN_INK_SWAP_MINUTES);
        }
        lastInkByDesign.set(dKey, fp);
    }

    // Calculate efficiency metrics
    const totalDTGTime = dtgJobs.reduce((sum, job) => sum + (Number(job.estimatedTime) || 0), 0);
    const totalDTFTime = dtfJobs.reduce((sum, job) => sum + (Number(job.estimatedTime) || 0), 0);
    const totalEmbroideryTime = embroideryJobs.reduce((sum, job) => sum + (Number(job.estimatedTime) || 0), 0);
    const totalScreenTime = screenPrintJobs.reduce((sum, job) => sum + (Number(job.estimatedTime) || 0), 0);
    
    const dtgEfficiency = Math.min((totalDTGTime / AVAILABLE_MINUTES) * 100, 100);
    const dtfEfficiency = Math.min((totalDTFTime / AVAILABLE_MINUTES) * 100, 100);
    const embroideryEfficiency = Math.min((totalEmbroideryTime / AVAILABLE_MINUTES) * 100, 100);
    const screenEfficiency = Math.min((totalScreenTime / AVAILABLE_MINUTES) * 100, 100);

    return {
        dtgJobs,
        dtfJobs,
        embroideryJobs,
        screenPrintJobs,
        dtgEfficiency,
        dtfEfficiency,
        embroideryEfficiency,
        screenEfficiency,
        totalEstimatedTime: totalDTGTime + totalDTFTime + totalEmbroideryTime + totalScreenTime
    };
};

/** Recompute a single line's estimated minutes (same math as the print list tables). */
const estimateLineMinutes = (line) => {
    if (!line) return 0;
    const result = calculateOptimalPrintlist([line]);
    const match =
      result.screenPrintJobs.find((j) => j.id === line.id) ||
      result.dtgJobs.find((j) => j.id === line.id) ||
      result.dtfJobs.find((j) => j.id === line.id) ||
      result.embroideryJobs.find((j) => j.id === line.id) ||
      result.screenPrintJobs[0] ||
      result.dtgJobs[0] ||
      result.dtfJobs[0] ||
      result.embroideryJobs[0];
    const mins = Number(match?.estimatedTime);
    return Number.isFinite(mins) ? Math.max(0, Math.round(mins)) : 0;
};

export {
    calculateScreenPrintTime,
    calculateDTGTime,
    calculateDTFTime,
    calculateEmbroideryTime,
    resolveEmbroideryStitchesPerPiece,
    EMBROIDERY_SETUP_MINUTES,
    EMBROIDERY_EFFECTIVE_SPM,
    EMBROIDERY_HEADS,
    EMBROIDERY_DEFAULT_STITCHES,
    canBeScreenPrinted,
    isDTGJob,
    isDTFJob,
    isEmbroideryJob,
    isScreenPrintJob,
    calculateOptimalPrintlist,
    estimateLineMinutes
}; 